import * as XLSX from 'xlsx';

/**
 * Processa arquivos Excel (.xlsx, .xls) e CSV e converte para texto estruturado
 */
export async function processSpreadsheet(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        if (!data) {
          reject(new Error('Erro ao ler arquivo'));
          return;
        }

        let workbook: XLSX.WorkBook;

        if (file.name.endsWith('.csv')) {
          // Processar CSV
          const text = new TextDecoder().decode(data as ArrayBuffer);
          workbook = XLSX.read(text, { type: 'string' });
        } else {
          // Processar Excel
          workbook = XLSX.read(data, { type: 'array' });
        }

        // Converter todas as abas para texto
        let result = `📊 Arquivo: ${file.name}\n\n`;

        workbook.SheetNames.forEach((sheetName, index) => {
          const worksheet = workbook.Sheets[sheetName];
          
          // Converter para array de arrays
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
          
          if (jsonData.length === 0) {
            return;
          }

          result += `📑 Aba: ${sheetName}\n`;
          result += `${'─'.repeat(50)}\n`;

          // Formatar dados em tabela
          jsonData.forEach((row, rowIndex) => {
            if (row.length === 0) return;
            
            const rowText = row
              .map(cell => cell !== null && cell !== undefined ? String(cell) : '')
              .join(' | ');
            
            result += `${rowText}\n`;
            
            // Adicionar linha separadora após o cabeçalho
            if (rowIndex === 0) {
              result += `${'-'.repeat(50)}\n`;
            }
          });

          result += `\n`;
        });

        result += `\n📊 Total de abas: ${workbook.SheetNames.length}\n`;
        
        resolve(result);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error('Erro ao ler arquivo'));
    };

    if (file.name.endsWith('.csv')) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  });
}

/**
 * Processa arquivos PDF e extrai o texto
 */
export async function processPDF(file: File): Promise<string> {
  try {
    // Carregar PDF.js dinamicamente
    const pdfjsLib = await import('pdfjs-dist');
    
    // Configurar worker usando arquivo local (copiado para /public)
    pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
    
    console.log('📄 PDF.js version:', pdfjsLib.version);
    console.log('📄 Worker URL:', pdfjsLib.GlobalWorkerOptions.workerSrc);
    
    // Ler arquivo como ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();
    
    // Carregar documento PDF
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    
    let fullText = `📄 Arquivo: ${file.name}\n`;
    fullText += `📑 Total de páginas: ${pdf.numPages}\n\n`;
    fullText += `${'─'.repeat(50)}\n\n`;
    
    // Extrair texto de cada página
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      
      const pageText = textContent.items
        .map((item: any) => item.str)
        .join(' ');
      
      if (pageText.trim()) {
        fullText += `📄 Página ${pageNum}:\n`;
        fullText += pageText + '\n\n';
      }
    }
    
    return fullText;
  } catch (error) {
    console.error('❌ Erro ao processar PDF:', error);
    throw new Error(`Não foi possível processar o PDF: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
  }
}

/**
 * Verifica se o arquivo é um PDF
 */
export function isPDF(file: File): boolean {
  return file.name.toLowerCase().endsWith('.pdf') || file.type === 'application/pdf';
}

/**
 * Verifica se o arquivo é uma planilha (Excel ou CSV)
 */
export function isSpreadsheet(file: File): boolean {
  const extension = file.name.toLowerCase();
  return extension.endsWith('.xlsx') || 
         extension.endsWith('.xls') || 
         extension.endsWith('.csv');
}

